/**
 * Sentry Error Tracking
 *
 * Initializes Sentry for error tracking and performance monitoring.
 * Gracefully no-ops if VITE_SENTRY_DSN is not set.
 */
import * as Sentry from "@sentry/react";
import type { BrowserOptions, Event } from "@sentry/react";
import { sanitizeAppRoute } from "@/utils/sanitizeAppRoute";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export const SENTRY_SANITIZE_MAX_DEPTH = 8;

const EMBEDDED_ROUTE_DECODE_ROUNDS = 3;
const REDACTED_EMBEDDED_ROUTE = "/:redacted";
const REDACTED_QUERY = "[redacted-query]";
const REDACTED_ROUTE_QUERY_VALUE = "redacted";
const KEY_DECODE_ROUNDS = 3;
const SENSITIVE_KEY_DENYLIST = new Set([
  "authorization",
  "constructor",
  "content",
  "cookie",
  "cookies",
  "email",
  "evidence",
  "evidenceprofile",
  "fragment",
  "hash",
  "ip",
  "ipaddress",
  "leaderid",
  "meetingid",
  "memo",
  "memotext",
  "note",
  "notes",
  "notetext",
  "philosophy",
  "philosophytext",
  "privatecontext",
  "profile",
  "prompt",
  "proto",
  "prototype",
  "query",
  "question",
  "questions",
  "reflection",
  "reflectiontext",
  "search",
  "searchparams",
  "source",
  "sourcebody",
  "sourcecontent",
  "sourcecontents",
  "sourcepayload",
  "sourcetext",
  "token",
  "transcript",
  "transcriptcontent",
  "transcriptid",
  "transcriptpayload",
  "evidenceid",
  "profileid",
  "sourceid",
]);

const REQUEST_PAYLOAD_KEY_DENYLIST = new Set(["body", "cookies", "data", "headers"]);
const USER_IDENTITY_KEY_ALLOWLIST = new Set(["id", "role"]);
const PERCENT_ENCODED_BYTE_PATTERN = /%[0-9a-f]{2}/i;
const SPAN_PAYLOAD_KEY_PATTERN = /^(?:http)?(?:request|response)?(?:headers?|body|authorization|cookies?|token)/;
const SENSITIVE_KEY_SEGMENTS = new Set([
  "authorization",
  "cookie",
  "evidence",
  "fragment",
  "hash",
  "memo",
  "note",
  "philosophy",
  "profile",
  "prompt",
  "query",
  "question",
  "reflection",
  "search",
  "email",
  "ip",
  "token",
  "transcript",
]);
const SAFE_METRIC_BLOCKING_SEGMENTS = new Set(["content", "id", "payload", "text", "value"]);
const SENSITIVE_NORMALIZED_FALLBACK_FRAGMENTS = [
  "authorization",
  "clientip",
  "contactemail",
  "cookie",
  "emailaddress",
  "evidence",
  "fragmentvalue",
  "hashvalue",
  "leaderid",
  "meetingid",
  "privatecontext",
  "profile",
  "prompt",
  "queryparams",
  "querypayload",
  "querystring",
  "question",
  "reflection",
  "remoteip",
  "searchparams",
  "sourcebody",
  "sourcecontent",
  "sourcedata",
  "sourceid",
  "sourcepayload",
  "sourcetext",
  "transcript",
  "useremail",
];

const UUID_LIKE_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const ENCODED_UUID_LIKE_PATTERN = /[0-9a-f]{8}(?:-|%(?:25){0,2}2d)[0-9a-f]{4}(?:-|%(?:25){0,2}2d)[0-9a-f]{4}(?:-|%(?:25){0,2}2d)[0-9a-f]{4}(?:-|%(?:25){0,2}2d)[0-9a-f]{12}/gi;
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s"'<>]+/gi;
const STANDALONE_ABSOLUTE_URL_PATTERN = /^https?:\/\/[^\s"'<>]+$/i;
const STANDALONE_APP_ROUTE_PATTERN = /^(?:\/|%(?:25)*(?:2f))[^\s"'<>]*$/i;
const COACH_MEETING_ROUTE_PATTERN = /\/coach\/meetings\/[^/?#\s"'<>]+(?:\/reflect)?(?:\?[^#\s"'<>]*)?(?:#[^\s"'<>]*)?/gi;
const ENCODED_OR_RAW_SLASH_PATTERN = String.raw`(?:/|%(?:25)*2f)`;
const ENCODED_COACH_MEETING_ROUTE_PREFIX = String.raw`${ENCODED_OR_RAW_SLASH_PATTERN}(?:c|%(?:25)*63)(?:o|%(?:25)*6f)(?:a|%(?:25)*61)(?:c|%(?:25)*63)(?:h|%(?:25)*68)${ENCODED_OR_RAW_SLASH_PATTERN}(?:m|%(?:25)*6d)(?:e|%(?:25)*65)(?:e|%(?:25)*65)(?:t|%(?:25)*74)(?:i|%(?:25)*69)(?:n|%(?:25)*6e)(?:g|%(?:25)*67)(?:s|%(?:25)*73)${ENCODED_OR_RAW_SLASH_PATTERN}`;
const ENCODED_COACH_MEETING_ROUTE_PATTERN = new RegExp(
  `${ENCODED_COACH_MEETING_ROUTE_PREFIX}[^\\s"'<>]*`,
  "gi"
);
const ENCODED_KNOWN_COACH_MEETING_ROUTE_PATTERN = new RegExp(
  `^${ENCODED_COACH_MEETING_ROUTE_PREFIX}`,
  "i"
);
const KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT = String.raw`[^/?#\s"'<>]+`;
const KNOWN_DYNAMIC_RELATIVE_ROUTE_BOUNDARY = String.raw`(?=$|[?#\s"'<>.,;:!?()[\]{}])`;
const KNOWN_DYNAMIC_RELATIVE_ROUTE_PATTERN = new RegExp(
  `(?:${[
      `/coach/leaders/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}/synthesis/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/leaders/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}/record`,
      `/coach/leaders/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/review/sequence/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/review/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/post-meeting/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/reflect/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/coach/meetings/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}/reflect`,
      `/coach/meetings/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/journey/summary/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/transcript-analysis/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/post-meeting/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/invite/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/join/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/nudge/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/assess/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/reflect/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
      `/meetings/${KNOWN_DYNAMIC_RELATIVE_ROUTE_SEGMENT}`,
    ].join("|")})(?:\\?[^#\\s"'<>]*)?(?:#[^\\s"'<>]*)?${KNOWN_DYNAMIC_RELATIVE_ROUTE_BOUNDARY}`,
  "gi",
);
const ASSIGNMENT_CANDIDATE_PATTERN = /[a-z0-9_.%~[\]-]+(?:=|%(?:25)*3d)(?:"(?:\\.|[^"\\]){0,2048}"|'(?:\\.|[^'\\]){0,2048}'|["'][\s\S]*$|[^\s"'<>]+)/gi;
const ASSIGNMENT_KEY_CHARACTER_PATTERN = /[a-z0-9_.%~[\]-]/i;
const ENCODED_ASSIGNMENT_KEY_PATTERN = /(?:^|&|%(?:25)*26)([a-z0-9_.%~[\]-]+)%(?:25)*3d/gi;
const UNQUOTED_ASSIGNMENT_VALUE_BOUNDARY_PATTERN = /[\s?&#]/;
const TRAILING_PROSE_PUNCTUATION_PATTERN = /[\])}.,;:!?]+$/;

type SentryOptionsInput = {
  dsn?: string;
  environment?: string;
  integrations?: BrowserOptions["integrations"];
};

type SentrySpan = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0];

type SanitizerState = {
  active: WeakSet<object>;
  allowRootUserIdentity: boolean;
  allowRootTransactionSource: boolean;
  depth: number;
  literalPath: string[];
  path: string[];
  spanPayload: boolean;
};

let initialized = false;
let runtimeEnabled = Boolean(SENTRY_DSN);

function decodeKeyToFixedPoint(key: string) {
  let decodedKey = key.normalize("NFKC");

  for (let round = 0; round < KEY_DECODE_ROUNDS; round += 1) {
    try {
      const nextKey = decodeURIComponent(decodedKey).normalize("NFKC");
      if (nextKey === decodedKey) break;
      decodedKey = nextKey;
    } catch {
      break;
    }
  }

  return decodedKey;
}

function normalizeKey(key: string) {
  return decodeKeyToFixedPoint(key).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function segmentKey(key: string) {
  return key
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((segment) => segment.toLowerCase());
}

function inspectKey(key: string) {
  const decoded = decodeKeyToFixedPoint(key);
  return {
    decoded,
    normalized: decoded.toLowerCase().replace(/[^a-z0-9]/g, ""),
    segments: segmentKey(decoded),
  };
}

function containsNonAscii(value: string) {
  return Array.from(value).some((character) => character.charCodeAt(0) > 0x7f);
}

function isRootRequestPath(path: string[]) {
  return path.length === 1 && path[0] === "request";
}

function hasSegmentSequence(segments: string[], first: string, second: string) {
  return segments.some((segment, index) => segment === first && segments[index + 1] === second);
}

function isSafeCoarseMetric(segments: string[], value: unknown) {
  if (typeof value !== "number" || !Number.isFinite(value)) return false;
  if (segments.some((segment) => SAFE_METRIC_BLOCKING_SEGMENTS.has(segment))) return false;

  const last = segments.at(-1);
  const previous = segments.at(-2);
  return (
    last === "count"
    || last === "duration"
    || (previous === "duration" && last === "ms")
    || (previous === "attempt" && last === "count")
  );
}

function hasSensitiveNormalizedFallback(normalized: string) {
  return (
    normalized.endsWith("token")
    || normalized.endsWith("tokenvalue")
    || normalized.endsWith("memo")
    || normalized.endsWith("note")
    || normalized === "clientipv4"
    || normalized === "ipaddress"
    || SENSITIVE_NORMALIZED_FALLBACK_FRAGMENTS.some((fragment) => normalized.includes(fragment))
  );
}

function isSensitiveKeyInspection(
  inspection: ReturnType<typeof inspectKey>,
  value?: unknown
) {
  const { normalized, segments } = inspection;
  if (isSafeCoarseMetric(segments, value)) return false;
  if (SENSITIVE_KEY_DENYLIST.has(normalized)) return true;
  if (segments.some((segment) => SENSITIVE_KEY_SEGMENTS.has(segment))) return true;
  if (hasSensitiveNormalizedFallback(normalized)) return true;

  return (
    hasSegmentSequence(segments, "meeting", "id")
    || hasSegmentSequence(segments, "leader", "id")
    || hasSegmentSequence(segments, "private", "context")
    || ["body", "content", "id", "payload", "text"].some((suffix) =>
      hasSegmentSequence(segments, "source", suffix)
    )
  );
}

function isSensitiveKey(key: string, value: unknown, state: SanitizerState) {
  const inspection = inspectKey(key);
  const { decoded, normalized } = inspection;
  if (PERCENT_ENCODED_BYTE_PATTERN.test(decoded) || containsNonAscii(decoded)) return true;
  if (
    state.allowRootTransactionSource
    &&
    normalized === "source"
    && key === "source"
    && state.literalPath.length === 1
    && state.literalPath[0] === "transaction_info"
  ) {
    return false;
  }
  if (normalized === "user") {
    return !(state.allowRootUserIdentity && state.path.length === 0 && key === "user");
  }
  if (
    state.allowRootUserIdentity
    && state.literalPath.length === 1
    && state.literalPath[0] === "user"
    && USER_IDENTITY_KEY_ALLOWLIST.has(normalized)
    && !USER_IDENTITY_KEY_ALLOWLIST.has(key)
  ) {
    return true;
  }
  if (isRootRequestPath(state.path) && REQUEST_PAYLOAD_KEY_DENYLIST.has(normalized)) return true;
  if (state.spanPayload && SPAN_PAYLOAD_KEY_PATTERN.test(normalized)) return true;

  return isSensitiveKeyInspection(inspection, value);
}

function isAllowedRootUserIdentityKey(state: SanitizerState, key: string) {
  return (
    state.allowRootUserIdentity
    && state.literalPath.length === 1
    && state.literalPath[0] === "user"
    && USER_IDENTITY_KEY_ALLOWLIST.has(key)
  );
}

function redactIdentifiers(value: string) {
  return value
    .replace(ENCODED_UUID_LIKE_PATTERN, "[redacted-id]")
    .replace(UUID_LIKE_PATTERN, "[redacted-id]");
}

function sanitizeEncodedCoachMeetingRoute(candidate: string) {
  let decoded = candidate;

  for (let round = 0; round <= EMBEDDED_ROUTE_DECODE_ROUNDS; round += 1) {
    if (decoded.toLowerCase().startsWith("/coach/meetings/")) {
      return sanitizeAppRoute(decoded);
    }
    if (round === EMBEDDED_ROUTE_DECODE_ROUNDS) break;

    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }

  return ENCODED_KNOWN_COACH_MEETING_ROUTE_PATTERN.test(decoded)
    ? REDACTED_EMBEDDED_ROUTE
    : candidate;
}

function sanitizeMatchedCandidate(
  candidate: string,
  sanitizer: (candidate: string) => string
) {
  const punctuation = candidate.match(TRAILING_PROSE_PUNCTUATION_PATTERN)?.[0] ?? "";
  const coreCandidate = punctuation ? candidate.slice(0, -punctuation.length) : candidate;
  return `${sanitizer(coreCandidate)}${punctuation}`;
}

function getAssignmentSeparatorLength(candidate: string, index: number) {
  if (candidate[index] === "=") return 1;
  if (candidate[index] !== "%") return 0;

  let cursor = index + 1;
  while (candidate.slice(cursor, cursor + 2).toLowerCase() === "25") cursor += 2;
  return candidate.slice(cursor, cursor + 2).toLowerCase() === "3d"
    ? cursor + 2 - index
    : 0;
}

function isSensitiveAssignmentKey(key: string) {
  const keyComponents = key.split(/[[\].]/).filter(Boolean);
  return keyComponents.some((component) => isSensitiveKeyInspection(inspectKey(component)));
}

function containsSensitiveAssignment(candidate: string) {
  let cursor = 0;

  while (cursor < candidate.length) {
    if (!ASSIGNMENT_KEY_CHARACTER_PATTERN.test(candidate[cursor])) {
      cursor += 1;
      continue;
    }

    const keyStart = cursor;
    let separatorLength = 0;
    while (cursor < candidate.length) {
      separatorLength = getAssignmentSeparatorLength(candidate, cursor);
      if (separatorLength > 0 || !ASSIGNMENT_KEY_CHARACTER_PATTERN.test(candidate[cursor])) break;
      cursor += 1;
    }

    if (separatorLength === 0) continue;
    if (cursor === keyStart) {
      cursor += separatorLength;
      continue;
    }

    if (isSensitiveAssignmentKey(candidate.slice(keyStart, cursor))) return true;
    cursor += separatorLength;

    const quote = candidate[cursor];
    if (quote === '"' || quote === "'") {
      cursor += 1;
      while (cursor < candidate.length) {
        if (candidate[cursor] === "\\") {
          cursor += cursor + 1 < candidate.length ? 2 : 1;
        } else if (candidate[cursor] === quote) {
          cursor += 1;
          break;
        } else {
          cursor += 1;
        }
      }
      continue;
    }

    while (
      cursor < candidate.length
      && !UNQUOTED_ASSIGNMENT_VALUE_BOUNDARY_PATTERN.test(candidate[cursor])
    ) {
      cursor += 1;
    }
  }

  return false;
}

function containsEncodedSensitiveAssignment(candidate: string) {
  for (const match of candidate.matchAll(ENCODED_ASSIGNMENT_KEY_PATTERN)) {
    if (isSensitiveKeyInspection(inspectKey(match[1] ?? ""))) return true;
  }
  return false;
}

function sanitizeEncodedSensitiveAssignments(candidate: string) {
  let decoded = candidate;

  for (let round = 0; round <= EMBEDDED_ROUTE_DECODE_ROUNDS; round += 1) {
    if (containsSensitiveAssignment(decoded)) return REDACTED_QUERY;
    if (round === EMBEDDED_ROUTE_DECODE_ROUNDS) break;

    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    } catch {
      break;
    }
  }

  return (
    PERCENT_ENCODED_BYTE_PATTERN.test(decoded)
    || containsEncodedSensitiveAssignment(decoded)
  ) ? REDACTED_QUERY : candidate;
}

function isAssignmentWithinRoute(value: string, offset: number) {
  const tokenPrefix = value.slice(0, offset).match(/[^\s<>]*$/)?.[0] ?? "";
  return (
    /(?:https?:\/\/|\/|%(?:25)*2f)/i.test(tokenPrefix)
    && /(?:[?&#]|%(?:25)*(?:3f|26|23))/i.test(tokenPrefix)
  );
}

function sanitizeAssignmentCandidate(candidate: string, offset: number, input: string) {
  const routeSanitized = sanitizeMatchedCandidate(candidate, sanitizeEncodedCoachMeetingRoute);
  if (routeSanitized !== candidate) return routeSanitized;

  const assignmentSanitized = sanitizeMatchedCandidate(
    candidate,
    sanitizeEncodedSensitiveAssignments
  );
  return assignmentSanitized.startsWith(REDACTED_QUERY) && isAssignmentWithinRoute(input, offset)
    ? `${REDACTED_ROUTE_QUERY_VALUE}${assignmentSanitized.slice(REDACTED_QUERY.length)}`
    : assignmentSanitized;
}

export function sanitizeSentryString(value: string) {
  const trimmed = value.trim();
  if (
    value === trimmed
    && (STANDALONE_ABSOLUTE_URL_PATTERN.test(value) || STANDALONE_APP_ROUTE_PATTERN.test(value))
  ) {
    return redactIdentifiers(sanitizeAppRoute(value));
  }

  const routeSanitized = value
    .replace(ASSIGNMENT_CANDIDATE_PATTERN, (query, offset: number, input: string) =>
      sanitizeAssignmentCandidate(query, offset, input)
    )
    .replace(ABSOLUTE_URL_PATTERN, (url) => sanitizeMatchedCandidate(url, sanitizeAppRoute))
    .replace(COACH_MEETING_ROUTE_PATTERN, (route) => sanitizeMatchedCandidate(route, sanitizeAppRoute))
    .replace(ENCODED_COACH_MEETING_ROUTE_PATTERN, (route) =>
      sanitizeMatchedCandidate(route, sanitizeEncodedCoachMeetingRoute)
    );

  return redactIdentifiers(
    routeSanitized.replace(
      KNOWN_DYNAMIC_RELATIVE_ROUTE_PATTERN,
      (route) => sanitizeMatchedCandidate(route, sanitizeAppRoute),
    ),
  );
}

function getOwnDataDescriptors(value: object) {
  try {
    return Object.getOwnPropertyDescriptors(value);
  } catch {
    return null;
  }
}

function sanitizeDate(value: Date) {
  try {
    return Date.prototype.toISOString.call(value);
  } catch {
    return "[Invalid Date]";
  }
}

function getPrototypeDataString(value: object, key: string) {
  try {
    const prototype = Object.getPrototypeOf(value);
    if (!prototype) return undefined;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, key);
    return descriptor && "value" in descriptor && typeof descriptor.value === "string"
      ? descriptor.value
      : undefined;
  } catch {
    return undefined;
  }
}

function sanitizeError(value: Error) {
  const descriptors = getOwnDataDescriptors(value);
  if (!descriptors) return { name: "Error", message: "[Unserializable]" };

  const name = descriptors.name && "value" in descriptors.name && typeof descriptors.name.value === "string"
    ? descriptors.name.value
    : getPrototypeDataString(value, "name") ?? "Error";
  const message = descriptors.message && "value" in descriptors.message && typeof descriptors.message.value === "string"
    ? descriptors.message.value
    : "";
  const stack = descriptors.stack && "value" in descriptors.stack && typeof descriptors.stack.value === "string"
    ? descriptors.stack.value
    : undefined;

  return {
    name: sanitizeSentryString(name),
    message: sanitizeSentryString(message),
    ...(stack ? { stack: sanitizeSentryString(stack) } : {}),
  };
}

function getObjectBrands(value: object) {
  try {
    return {
      isDate: value instanceof Date,
      isError: value instanceof Error,
    };
  } catch {
    return null;
  }
}

function sanitizeRecursive(value: unknown, state: SanitizerState): unknown {
  if (typeof value === "string") return sanitizeSentryString(value);
  if (typeof value === "bigint") return value.toString();
  if (value === null || typeof value !== "object") return value;
  if (state.depth >= SENTRY_SANITIZE_MAX_DEPTH) return "[Truncated]";
  const brands = getObjectBrands(value);
  if (!brands) return "[Unserializable]";
  if (brands.isDate) return sanitizeDate(value as Date);
  if (brands.isError) return sanitizeError(value as Error);
  if (state.active.has(value)) return "[Circular]";

  const descriptors = getOwnDataDescriptors(value);
  if (!descriptors) return "[Unserializable]";

  state.active.add(value);
  try {
    if (Array.isArray(value)) {
      const output: unknown[] = [];
      for (const [key, descriptor] of Object.entries(descriptors)) {
        if (!descriptor.enumerable || !("value" in descriptor) || !/^\d+$/.test(key)) continue;
        output[Number(key)] = sanitizeRecursive(descriptor.value, {
          ...state,
          depth: state.depth + 1,
          literalPath: [...state.literalPath, key],
          path: [...state.path, key],
        });
      }
      return output;
    }

    const output: Record<string, unknown> = {};
    for (const [key, descriptor] of Object.entries(descriptors)) {
      if (
        !descriptor.enumerable
        || !("value" in descriptor)
        || isSensitiveKey(key, descriptor.value, state)
      ) continue;

      const outputKey = sanitizeSentryString(key);
      if (Object.prototype.hasOwnProperty.call(output, outputKey)) continue;

      let outputValue: unknown;
      if (typeof descriptor.value === "string" && isAllowedRootUserIdentityKey(state, key)) {
        outputValue = descriptor.value;
      } else {
        const spanPayload = state.spanPayload || (state.path.length === 0 && key === "spans");
        outputValue = sanitizeRecursive(descriptor.value, {
          ...state,
          depth: state.depth + 1,
          literalPath: [...state.literalPath, key],
          path: [...state.path, normalizeKey(outputKey)],
          spanPayload,
        });
      }

      Object.defineProperty(output, outputKey, {
        configurable: true,
        enumerable: true,
        value: outputValue,
        writable: true,
      });
    }

    return output;
  } finally {
    state.active.delete(value);
  }
}

function clampDepth(depth: number) {
  if (Number.isNaN(depth) || depth === Number.NEGATIVE_INFINITY) return 0;
  if (depth === Number.POSITIVE_INFINITY) return SENTRY_SANITIZE_MAX_DEPTH;
  return Math.min(SENTRY_SANITIZE_MAX_DEPTH, Math.max(0, Math.trunc(depth)));
}

function sanitizeRoot(
  value: unknown,
  depth: number,
  allowRootUserIdentity: boolean,
  allowRootTransactionSource = false,
  spanPayload = false
) {
  return sanitizeRecursive(value, {
    active: new WeakSet<object>(),
    allowRootUserIdentity,
    allowRootTransactionSource,
    depth: clampDepth(depth),
    literalPath: [],
    path: [],
    spanPayload,
  });
}

export function sanitizeSentryValue(value: unknown, depth = 0) {
  return sanitizeRoot(value, depth, false);
}

function sanitizeEvent<T extends Event>(event: T): T {
  return sanitizeRoot(event, 0, true, true) as T;
}

export function sanitizeSentryEvent(event: Event) {
  return sanitizeEvent(event);
}

export function sanitizeSentryTransaction(event: Event) {
  return sanitizeEvent(event);
}

export function sanitizeSentrySpan(span: SentrySpan): SentrySpan {
  return sanitizeRoot(span, 0, false, false, true) as SentrySpan;
}

export function sanitizeSentryContext(context?: Record<string, unknown>) {
  if (!context) return undefined;
  return sanitizeRoot(context, 0, false) as Record<string, unknown>;
}

export function buildSentryOptions(input: SentryOptionsInput = {}): BrowserOptions {
  const dsn = input.dsn ?? SENTRY_DSN;

  return {
    dsn,
    environment: input.environment ?? import.meta.env.MODE ?? "production",
    enabled: Boolean(dsn),
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    integrations: input.integrations ?? [Sentry.browserTracingIntegration()],
    beforeSend: sanitizeSentryEvent,
    beforeSendSpan: sanitizeSentrySpan,
    beforeSendTransaction: sanitizeSentryTransaction,
  };
}

export function initSentry(input: SentryOptionsInput = {}) {
  const dsn = input.dsn ?? SENTRY_DSN;
  if (!dsn || initialized) return;

  const options = buildSentryOptions({ ...input, dsn });
  Sentry.init(options);
  initialized = true;
  runtimeEnabled = true;
}

export function setSentryUser(userId: string, role?: string) {
  if (!runtimeEnabled) return;
  Sentry.setUser({ id: userId, role });
}

export function clearSentryUser() {
  if (!runtimeEnabled) return;
  Sentry.setUser(null);
}

export function captureError(error: unknown, context?: Record<string, unknown>) {
  if (!runtimeEnabled) return;
  const extra = sanitizeSentryContext(context);

  if (error instanceof Error) {
    Sentry.captureException(error, { extra });
  } else {
    Sentry.captureMessage(sanitizeSentryString(String(error)), { level: "error", extra });
  }
}

export function addBreadcrumb(message: string, category?: string, data?: Record<string, unknown>) {
  if (!runtimeEnabled) return;
  const breadcrumb = sanitizeSentryValue({
    message,
    category,
    data,
    level: "info",
  });

  Sentry.addBreadcrumb(breadcrumb as Parameters<typeof Sentry.addBreadcrumb>[0]);
}

export { Sentry };
